import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import { Layout } from '../components/Layout';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { Container } from '../components/Container';

const AuthPage = lazy(() =>
  import('../pages/AuthPage').then(module => ({ default: module.AuthPage }))
);
const ClubPickerPage = lazy(() =>
  import('../pages/ClubPickerPage').then(module => ({ default: module.ClubPickerPage }))
);
const DashboardPage = lazy(() =>
  import('../pages/DashboardPage').then(module => ({ default: module.DashboardPage }))
);
const CustomersPage = lazy(() =>
  import('../pages/CustomersPage').then(module => ({ default: module.CustomersPage }))
);
const SubscriptionsPage = lazy(() =>
  import('../pages/SubscriptionsPage').then(module => ({ default: module.SubscriptionsPage }))
);
const PaymentsPage = lazy(() =>
  import('../pages/PaymentsPage').then(module => ({ default: module.PaymentsPage }))
);
const RolesPage = lazy(() =>
  import('../pages/RolesPage').then(module => ({ default: module.RolesPage }))
);
const IndustrySettingsPage = lazy(() =>
  import('../pages/IndustrySettingsPage').then(module => ({ default: module.IndustrySettingsPage }))
);
const AgreementSettingsPage = lazy(() =>
  import('../pages/AgreementSettingsPage').then(module => ({ default: module.AgreementSettingsPage }))
);
const PlatformDashboardPage = lazy(() =>
  import('../pages/PlatformDashboardPage').then(module => ({ default: module.PlatformDashboardPage }))
);
const ClubsListPage = lazy(() =>
  import('../pages/ClubsListPage').then(module => ({ default: module.ClubsListPage }))
);
const ClubDetailPage = lazy(() =>
  import('../pages/ClubDetailPage').then(module => ({ default: module.ClubDetailPage }))
);
const PlatformSettingsPage = lazy(() =>
  import('../pages/PlatformSettingsPage').then(module => ({ default: module.PlatformSettingsPage }))
);

interface ProtectedRouteProps {
  children: React.ReactElement;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner fullScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  return children;
};

interface TenantGuardProps {
  children: React.ReactElement;
}

const TenantGuard: React.FC<TenantGuardProps> = ({ children }) => {
  const { activeClub, availableClubs, isLoading } = useTenant();

  if (isLoading) {
    return <LoadingSpinner fullScreen />;
  }

  if (availableClubs.length === 0) {
    return (
      <Layout>
        <Container maxWidth="md">
          <div style={{ textAlign: 'center', padding: '4rem 1rem' }}>
            <h1>No Access</h1>
            <p>You do not have access to any clubs. Please contact your administrator.</p>
          </div>
        </Container>
      </Layout>
    );
  }

  if (!activeClub) {
    return <Navigate to="/pick-club" replace />;
  }

  return children;
};

interface AdminGuardProps {
  children: React.ReactElement;
}

const AdminGuard: React.FC<AdminGuardProps> = ({ children }) => {
  const { isPlatformAdmin, isLoading } = useTenant();

  if (isLoading) {
    return <LoadingSpinner fullScreen />;
  }

  if (!isPlatformAdmin) {
    return (
      <Layout>
        <Container maxWidth="md">
          <div style={{ textAlign: 'center', padding: '4rem 1rem' }}>
            <h1>Unauthorized</h1>
            <p>You do not have permission to access the platform administration area.</p>
          </div>
        </Container>
      </Layout>
    );
  }

  return children;
};

export const AppRoutes: React.FC = () => {
  const { isAuthenticated } = useAuth();

  return (
    <Suspense fallback={<LoadingSpinner fullScreen />}>
      <Routes>
        <Route
          path="/auth"
          element={
            isAuthenticated ? (
              <Navigate to="/pick-club" replace />
            ) : (
              <AuthPage />
            )
          }
        />
        <Route
          path="/pick-club"
          element={
            <ProtectedRoute>
              <Layout>
                <ClubPickerPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminGuard>
                <PlatformDashboardPage />
              </AdminGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/clubs"
          element={
            <ProtectedRoute>
              <AdminGuard>
                <ClubsListPage />
              </AdminGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/clubs/:clubId"
          element={
            <ProtectedRoute>
              <AdminGuard>
                <ClubDetailPage />
              </AdminGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/settings"
          element={
            <ProtectedRoute>
              <AdminGuard>
                <PlatformSettingsPage />
              </AdminGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <TenantGuard>
                <Layout>
                  <DashboardPage />
                </Layout>
              </TenantGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/customers"
          element={
            <ProtectedRoute>
              <TenantGuard>
                <Layout>
                  <CustomersPage />
                </Layout>
              </TenantGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/customers/:customerId"
          element={
            <ProtectedRoute>
              <TenantGuard>
                <Layout>
                  <CustomersPage />
                </Layout>
              </TenantGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/subscriptions"
          element={
            <ProtectedRoute>
              <TenantGuard>
                <Layout>
                  <SubscriptionsPage />
                </Layout>
              </TenantGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/subscriptions/:subscriptionId"
          element={
            <ProtectedRoute>
              <TenantGuard>
                <Layout>
                  <SubscriptionsPage />
                </Layout>
              </TenantGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/payments"
          element={
            <ProtectedRoute>
              <TenantGuard>
                <Layout>
                  <PaymentsPage />
                </Layout>
              </TenantGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/payments/:paymentId"
          element={
            <ProtectedRoute>
              <TenantGuard>
                <Layout>
                  <PaymentsPage />
                </Layout>
              </TenantGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/roles"
          element={
            <ProtectedRoute>
              <TenantGuard>
                <RolesPage />
              </TenantGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/industry"
          element={
            <ProtectedRoute>
              <TenantGuard>
                <IndustrySettingsPage />
              </TenantGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/agreement"
          element={
            <ProtectedRoute>
              <TenantGuard>
                <AgreementSettingsPage />
              </TenantGuard>
            </ProtectedRoute>
          }
        />
        <Route path="/settings" element={<Navigate to="/settings/roles" replace />} />
        {/* Root should go to auth (authenticated users will be redirected to pick-club) */}
        <Route path="/" element={<Navigate to="/auth" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
};
